import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const CourseComparison = ({ courses, onRemoveCourse, onClearAll, isOpen, onToggle }) => {
  if (!courses?.length) return null;

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 lg:hidden"
          onClick={onToggle}
        />
      )}
      {/* Comparison Panel */}
      <div className={`
        fixed bottom-0 left-0 right-0 lg:relative lg:bottom-auto
        bg-card border-t lg:border border-border lg:rounded-xl
        transform transition-transform duration-300 ease-in-out z-50 lg:z-auto
        max-h-[80vh] lg:max-h-none overflow-y-auto
        ${isOpen ? 'translate-y-0' : 'translate-y-full lg:translate-y-0'}
      `}>
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Icon name="GitCompare" size={20} className="text-primary" />
              <div>
                <h3 className="font-semibold text-foreground">Course Comparison</h3>
                <p className="text-sm text-muted-foreground">
                  {courses?.length} course{courses?.length !== 1 ? 's' : ''} selected
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearAll}
                iconName="Trash2"
                iconPosition="left"
              >
                Clear All
              </Button>
              <button
                onClick={onToggle}
                className="lg:hidden p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Comparison Content */}
        <div className="p-4">
          <div className="overflow-x-auto">
            <div className="min-w-full">
              {/* Course Headers */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                {courses?.map((course) => (
                  <div key={course?.id} className="relative">
                    <div className="bg-muted/50 rounded-lg p-4">
                      <button
                        onClick={() => onRemoveCourse(course?.id)}
                        className="absolute top-2 right-2 p-1 hover:bg-background rounded-full transition-colors"
                        title="Remove from comparison"
                      >
                        <Icon name="X" size={16} />
                      </button>
                      
                      <div className="flex items-start space-x-3">
                        <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                          <Image
                            src={course?.image}
                            alt={course?.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-foreground text-sm line-clamp-2">
                            {course?.title}
                          </h4>
                          <p className="text-xs text-muted-foreground mt-1">
                            {course?.category}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Comparison Table */}
              <div className="space-y-4">
                {/* Duration */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="lg:col-span-3">
                    <h5 className="font-medium text-foreground mb-2 flex items-center">
                      <Icon name="Clock" size={16} className="mr-2" />
                      Duration
                    </h5>
                  </div>
                  {courses?.map((course) => (
                    <div key={`duration-${course?.id}`} className="bg-muted/30 rounded-lg p-3">
                      <p className="font-medium text-foreground">{course?.duration}</p>
                    </div>
                  ))}
                </div>

                {/* Level */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="lg:col-span-3">
                    <h5 className="font-medium text-foreground mb-2 flex items-center">
                      <Icon name="BarChart3" size={16} className="mr-2" />
                      Level
                    </h5>
                  </div>
                  {courses?.map((course) => (
                    <div key={`level-${course?.id}`} className="bg-muted/30 rounded-lg p-3">
                      <p className="font-medium text-foreground">{course?.level}</p>
                    </div>
                  ))}
                </div>

                {/* Fee */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="lg:col-span-3">
                    <h5 className="font-medium text-foreground mb-2 flex items-center">
                      <Icon name="IndianRupee" size={16} className="mr-2" />
                      Course Fee
                    </h5>
                  </div>
                  {courses?.map((course) => (
                    <div key={`fee-${course?.id}`} className="bg-muted/30 rounded-lg p-3">
                      <p className="font-bold text-lg text-foreground">₹{course?.fees?.toLocaleString()}</p>
                    </div>
                  ))}
                </div>

                {/* Mode */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="lg:col-span-3">
                    <h5 className="font-medium text-foreground mb-2 flex items-center">
                      <Icon name="Monitor" size={16} className="mr-2" />
                      Learning Mode
                    </h5>
                  </div>
                  {courses?.map((course) => (
                    <div key={`mode-${course?.id}`} className="bg-muted/30 rounded-lg p-3">
                      <p className="font-medium text-foreground">{course?.mode}</p>
                    </div>
                  ))}
                </div>

                {/* Certification */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="lg:col-span-3">
                    <h5 className="font-medium text-foreground mb-2 flex items-center">
                      <Icon name="Award" size={16} className="mr-2" />
                      Certification
                    </h5>
                  </div>
                  {courses?.map((course) => (
                    <div key={`cert-${course?.id}`} className="bg-muted/30 rounded-lg p-3">
                      <p className="font-medium text-foreground">{course?.certification}</p>
                    </div>
                  ))}
                </div>

                {/* Placement Rate */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="lg:col-span-3">
                    <h5 className="font-medium text-foreground mb-2 flex items-center">
                      <Icon name="TrendingUp" size={16} className="mr-2" />
                      Placement Rate
                    </h5>
                  </div>
                  {courses?.map((course) => (
                    <div key={`placement-${course?.id}`} className="bg-muted/30 rounded-lg p-3">
                      <p className="font-bold text-lg text-success">{course?.placementRate}%</p>
                    </div>
                  ))}
                </div>

                {/* Average Salary */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="lg:col-span-3">
                    <h5 className="font-medium text-foreground mb-2 flex items-center">
                      <Icon name="DollarSign" size={16} className="mr-2" />
                      Average Salary
                    </h5>
                  </div>
                  {courses?.map((course) => (
                    <div key={`salary-${course?.id}`} className="bg-muted/30 rounded-lg p-3">
                      <p className="font-bold text-lg text-primary">₹{course?.avgSalary}</p>
                    </div>
                  ))}
                </div>

                {/* Key Skills */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="lg:col-span-3">
                    <h5 className="font-medium text-foreground mb-2 flex items-center">
                      <Icon name="Code" size={16} className="mr-2" />
                      Key Skills
                    </h5>
                  </div>
                  {courses?.map((course) => (
                    <div key={`skills-${course?.id}`} className="bg-muted/30 rounded-lg p-3">
                      <div className="flex flex-wrap gap-1">
                        {course?.skills?.slice(0, 4)?.map((skill, index) => (
                          <span
                            key={index}
                            className="bg-primary/10 text-primary px-2 py-1 rounded text-xs"
                          >
                            {skill}
                          </span>
                        ))}
                        {course?.skills?.length > 4 && (
                          <span className="text-xs text-muted-foreground px-2 py-1">
                            +{course?.skills?.length - 4}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 pt-4 border-t border-border">
                  {courses?.map((course) => (
                    <div key={`action-${course?.id}`} className="space-y-2">
                      <Button
                        variant="outline"
                        size="sm"
                        fullWidth
                        iconName="Eye"
                        iconPosition="left"
                      >
                        View Details
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        fullWidth
                        iconName="UserPlus"
                        iconPosition="left"
                      >
                        Apply Now
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CourseComparison;